/**
 * Users tab for the Lupa Admin panel. Lists users, lets the admin create new
 * users (with role + client_access pre-set) and edit existing ones (role,
 * client_access, display_name, is_active, password reset).
 *
 * Lives inside RagAdminPage's tab strip. Requires role='dwa_admin' on the
 * caller — RagAdminPage already gates entry.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  type AdminCreateUserRequest,
  type AdminUpdateUserRequest,
} from '../../services/adminApi';
import { EmailRecipientsPanel } from './EmailRecipientsPanel';
import { DWA_INTERNAL_DEPARTMENTS } from '../../pages/dwaInternalCatalogue';
import {
  fetchPortfolios,
  type PortfolioDef,
  type BackendClientId,
} from '../../services/dashboardApi';
import type { User, UserRole, ClientAccessEntry, UserPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { clientAccessForSave } from './clientAccess';

/** Sub-permissions (admins only, each independent). Only a super admin can change them. */
const PERMISSION_OPTIONS: { value: UserPermission; label: string; help: string }[] = [
  { value: 'super_admin', label: 'Super admin', help: 'Can grant and remove these permissions for other admins.' },
  { value: 'technical', label: 'Technical', help: 'Sees the Technical details in the Omega Wolf Guide (how features are built and run).' },
];

const ROLE_OPTIONS: { value: UserRole; label: string; help: string }[] = [
  { value: 'dwa_admin', label: 'DWA Admin', help: 'Full access — sees all clients/portfolios, Lupa chat, and this admin panel.' },
  { value: 'dwa_standard', label: 'DWA Standard', help: 'Internal DWA user. Sees all clients/portfolios + Lupa chat. No admin panel.' },
  { value: 'client', label: 'Client', help: 'External client user. Dashboard-only — no Lupa, no portfolio strip. Must be assigned at least one client.' },
];

const CLIENT_OPTIONS: { id: BackendClientId; label: string }[] = [
  { id: 'morningstar', label: 'MorningStar Senior Living' },
  { id: 'plc', label: 'Priority Life Care' },
  { id: 'dwa-internal', label: 'DWA Internal' },
];

// ─── Styles (consistent with the rest of RagAdminPage) ──────────────────────

const card: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  padding: '1.25rem 1.5rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  fontSize: '0.68rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
  fontWeight: 600,
  borderBottom: '2px solid #e2e8f0',
};

const td: React.CSSProperties = {
  padding: '0.55rem 0.6rem',
  borderBottom: '1px solid #f1f5f9',
  color: '#1a202c',
};

const button = (variant: 'primary' | 'secondary' | 'danger'): React.CSSProperties => ({
  fontSize: '0.78rem',
  fontWeight: 600,
  padding: '0.4rem 0.85rem',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: variant === 'primary' ? '#fec35c' : 'transparent',
  color: variant === 'primary' ? '#1a202c' : variant === 'danger' ? '#dc2626' : '#1a202c',
  border: `1px solid ${variant === 'primary' ? '#fec35c' : variant === 'danger' ? '#dc2626' : '#cbd5e1'}`,
});

const input: React.CSSProperties = {
  fontSize: '0.85rem',
  padding: '0.4rem 0.55rem',
  borderRadius: 4,
  border: '1px solid #cbd5e1',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const label: React.CSSProperties = {
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
  fontWeight: 600,
  marginBottom: '0.25rem',
  display: 'block',
};

// ─── Helpers ────────────────────────────────────────────────────────────────


function summarizeAccess(user: User): string {
  if (user.role !== 'client') return 'unrestricted';
  if (!user.client_access || user.client_access.length === 0) return 'no clients assigned';
  return user.client_access
    .map((e) => `${e.client_id} (${e.portfolio_keys.length} portfolio${e.portfolio_keys.length === 1 ? '' : 's'})`)
    .join(', ');
}

// ─── Form (used for both create + edit) ─────────────────────────────────────

interface UserFormState {
  email: string;
  password: string;
  display_name: string;
  role: UserRole;
  client_access: ClientAccessEntry[];
  is_active: boolean;
  permissions: UserPermission[];
}

interface UserFormProps {
  mode: 'create' | 'edit';
  initial: UserFormState;
  /** Map of clientId → portfolios available for assignment. Loaded once on mount. */
  portfoliosByClient: Record<string, PortfolioDef[]>;
  isSelf: boolean;
  /** The signed-in admin is a super admin, so may change permissions. */
  canGrant: boolean;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (state: UserFormState) => void;
}

const UserForm: React.FC<UserFormProps> = ({
  mode, initial, portfoliosByClient, isSelf, canGrant, saving, error, onCancel, onSubmit,
}) => {
  const [state, setState] = useState<UserFormState>(initial);

  const set = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const addClientEntry = () => {
    // Pick the first client not already in client_access.
    const used = new Set(state.client_access.map((e) => e.client_id));
    const candidate = CLIENT_OPTIONS.find((c) => !used.has(c.id));
    if (!candidate) return;
    set('client_access', [...state.client_access, { client_id: candidate.id, portfolio_keys: [] }]);
  };

  const removeClientEntry = (idx: number) => {
    set('client_access', state.client_access.filter((_, i) => i !== idx));
  };

  const updateClientEntry = (idx: number, patch: Partial<ClientAccessEntry>) => {
    const next = [...state.client_access];
    next[idx] = { ...next[idx], ...patch };
    set('client_access', next);
  };

  const togglePortfolio = (idx: number, portfolioKey: string) => {
    const entry = state.client_access[idx];
    const has = entry.portfolio_keys.includes(portfolioKey);
    const portfolio_keys = has
      ? entry.portfolio_keys.filter((k) => k !== portfolioKey)
      : [...entry.portfolio_keys, portfolioKey];
    updateClientEntry(idx, { portfolio_keys });
  };

  // ── DWA Internal dashboard access (Phase 2) ────────────────────────────
  // For dwa_standard users, admins can scope which DWA Internal
  // dashboards the user can see. Implemented by managing the
  // `dashboards` field on the dwa-internal entry of client_access.
  // If the field is OMITTED entirely (no checkboxes ever toggled),
  // the backend treats the user as "all access" (legacy default-allow).
  // Once any checkbox is toggled, the field becomes an explicit
  // allowlist — empty means default-deny.
  const dwaEntryIdx = state.client_access.findIndex((e) => e.client_id === 'dwa-internal');
  const dwaDashboards = dwaEntryIdx >= 0 ? state.client_access[dwaEntryIdx].dashboards : undefined;
  const dwaDashboardsScoped = dwaDashboards !== undefined;

  const toggleDwaDashboard = (slug: string) => {
    const list = dwaDashboards ?? [];
    const has = list.includes(slug);
    const next = has ? list.filter((d) => d !== slug) : [...list, slug];
    if (dwaEntryIdx >= 0) {
      updateClientEntry(dwaEntryIdx, { dashboards: next });
    } else {
      // No dwa-internal entry yet — add one with the dashboards field.
      set('client_access', [
        ...state.client_access,
        { client_id: 'dwa-internal', portfolio_keys: [], dashboards: next },
      ]);
    }
  };

  const resetDwaDashboardScoping = () => {
    // Remove the `dashboards` field entirely — reverts to legacy default-allow.
    if (dwaEntryIdx < 0) return;
    const next = [...state.client_access];
    const entry = { ...next[dwaEntryIdx] };
    delete entry.dashboards;
    next[dwaEntryIdx] = entry;
    set('client_access', next);
  };

  const enableDwaDashboardScoping = () => {
    // Add an empty dashboards array to switch into explicit allowlist mode.
    if (dwaEntryIdx >= 0) {
      updateClientEntry(dwaEntryIdx, { dashboards: [] });
    } else {
      set('client_access', [
        ...state.client_access,
        { client_id: 'dwa-internal', portfolio_keys: [], dashboards: [] },
      ]);
    }
  };

  const showClientAccess = state.role === 'client';
  const showDwaDashboardAccess = state.role === 'dwa_standard';

  return (
    <div style={{ ...card, marginBottom: '1rem' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', color: '#1a202c' }}>
        {mode === 'create' ? 'Create User' : `Edit ${initial.email}`}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div>
          <label style={label}>Email</label>
          <input
            type="email"
            style={input}
            value={state.email}
            onChange={(e) => set('email', e.target.value)}
            disabled={mode === 'edit'}
          />
        </div>
        <div>
          <label style={label}>Display Name</label>
          <input
            type="text"
            style={input}
            value={state.display_name}
            onChange={(e) => set('display_name', e.target.value)}
          />
        </div>
        <div>
          <label style={label}>{mode === 'create' ? 'Initial Password' : 'New Password (leave blank to keep)'}</label>
          <input
            type="password"
            style={input}
            value={state.password}
            onChange={(e) => set('password', e.target.value)}
            placeholder={mode === 'edit' ? '••••••' : ''}
          />
        </div>
        <div>
          <label style={label}>Role</label>
          <select
            style={input}
            value={state.role}
            onChange={(e) => set('role', e.target.value as UserRole)}
            disabled={isSelf}
            title={isSelf ? "You can't change your own role" : undefined}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0.6rem 0 1rem' }}>
        {ROLE_OPTIONS.find((r) => r.value === state.role)?.help}
      </p>

      {state.role === 'dwa_admin' && (
        <div style={{ marginBottom: '1rem' }} data-testid="user-permissions">
          <label style={label}>Permissions</label>
          {PERMISSION_OPTIONS.map((p) => {
            const checked = state.permissions.includes(p.value);
            // A super admin can't remove their own super admin (no lock-out).
            const locked = !canGrant || (isSelf && p.value === 'super_admin' && checked);
            return (
              <label
                key={p.value}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.35rem', cursor: locked ? 'not-allowed' : 'pointer' }}
                title={!canGrant ? 'Only a super admin can change permissions' : locked ? "You can't remove your own super admin" : undefined}
              >
                <input
                  type="checkbox"
                  aria-label={`${p.label} permission`}
                  checked={checked}
                  disabled={locked}
                  onChange={() => set('permissions', checked ? state.permissions.filter((x) => x !== p.value) : [...state.permissions, p.value])}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  <strong>{p.label}</strong>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>{p.help}</span>
                </span>
              </label>
            );
          })}
          {!canGrant && (
            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0.2rem 0 0' }}>Only a super admin can change these.</p>
          )}
        </div>
      )}

      {mode === 'edit' && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: isSelf ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox"
              checked={state.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              disabled={isSelf}
            />
            Account active
          </label>
        </div>
      )}

      {showClientAccess && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={label}>Client Access</label>
          <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.5rem' }}>
            Each entry: pick a client, then explicitly grant the portfolios this user can see.
            Empty portfolio list = no access in that client.
          </p>
          {state.client_access.length === 0 && (
            <p style={{ fontSize: '0.78rem', color: '#dc2626', margin: '0 0 0.5rem' }}>
              Client-role users must have at least one client entry.
            </p>
          )}
          {state.client_access.map((entry, idx) => {
            const portfolios = portfoliosByClient[entry.client_id] || [];
            return (
              <div
                key={idx}
                style={{
                  border: '1px solid #e2e8f0', borderRadius: 4,
                  padding: '0.75rem', marginBottom: '0.5rem',
                  background: '#fafbfc',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <select
                    style={{ ...input, width: 'auto', flex: 1 }}
                    value={entry.client_id}
                    onChange={(e) => updateClientEntry(idx, { client_id: e.target.value, portfolio_keys: [] })}
                  >
                    {CLIENT_OPTIONS.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <button type="button" style={button('danger')} onClick={() => removeClientEntry(idx)}>
                    Remove
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.35rem' }}>
                  {portfolios.length === 0 && (
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
                      No portfolios available for this client.
                    </p>
                  )}
                  {portfolios.map((p) => (
                    <label
                      key={p.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        fontSize: '0.8rem', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={entry.portfolio_keys.includes(p.key)}
                        onChange={() => togglePortfolio(idx, p.key)}
                      />
                      {p.label}
                      <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                        ({p.community_uniques.length})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          <button type="button" style={button('secondary')} onClick={addClientEntry}>
            + Add Client
          </button>
        </div>
      )}

      {showDwaDashboardAccess && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={label}>DWA Internal Dashboard Access</label>
          <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.5rem' }}>
            Restrict which DWA Internal dashboards this user can see. By default,
            dwa_standard users see all dashboards (legacy). Toggle scoping below to
            switch to an explicit allowlist — anything unchecked becomes inaccessible.
          </p>

          {!dwaDashboardsScoped ? (
            <div
              style={{
                border: '1px solid #e2e8f0', borderRadius: 4,
                padding: '0.75rem', marginBottom: '0.5rem',
                background: '#fafbfc',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
              }}
            >
              <span style={{ fontSize: '0.82rem', color: '#4a5568' }}>
                Currently: <strong>All dashboards visible</strong> (no scoping applied)
              </span>
              <button type="button" style={button('secondary')} onClick={enableDwaDashboardScoping}>
                Enable scoping
              </button>
            </div>
          ) : (
            <div
              style={{
                border: '1px solid #e2e8f0', borderRadius: 4,
                padding: '0.75rem', marginBottom: '0.5rem',
                background: '#fafbfc',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.6rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                  Checked = user can see this dashboard. Unchecked = denied.
                </p>
                <button type="button" style={button('secondary')} onClick={resetDwaDashboardScoping}>
                  Reset to all
                </button>
              </div>
              {DWA_INTERNAL_DEPARTMENTS.filter((d) => d.dashboards.length > 0).map((dept) => (
                <div key={dept.slug} style={{ marginBottom: '0.6rem' }}>
                  <h4 style={{
                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: '#64748b',
                    margin: '0 0 0.3rem',
                  }}>
                    {dept.name}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.3rem' }}>
                    {dept.dashboards.map((d) => (
                      <label
                        key={d.slug}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          fontSize: '0.8rem', cursor: 'pointer',
                        }}
                        title={d.description ?? ''}
                      >
                        <input
                          type="checkbox"
                          checked={(dwaDashboards ?? []).includes(d.slug)}
                          onChange={() => toggleDwaDashboard(d.slug)}
                        />
                        {d.name}
                        {d.status === 'coming-soon' && (
                          <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>(soon)</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {(dwaDashboards ?? []).length === 0 && (
                <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '0.4rem 0 0' }}>
                  Scoping is enabled but no dashboards are selected — this user can't see any DWA dashboards.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.5rem 0' }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" style={button('secondary')} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          style={button('primary')}
          onClick={() => onSubmit(state)}
          disabled={saving}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Create User' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ─── Main tab component ─────────────────────────────────────────────────────

interface UsersTabProps {
  /** Current admin's user id — disables self-demotion / self-deactivation. */
  currentUserId: string | null;
}

type SubTab = 'users' | 'recipients';

export const UsersTab: React.FC<UsersTabProps> = ({ currentUserId }) => {
  const { user: me } = useAuth();
  const canGrant = me?.role === 'dwa_admin' && (me.permissions ?? []).includes('super_admin');
  const [subTab, setSubTab] = useState<SubTab>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [portfoliosByClient, setPortfoliosByClient] = useState<Record<string, PortfolioDef[]>>({});

  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listUsers(includeInactive);
      setUsers(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { reload(); }, [reload]);

  // Load portfolios for every client once so the form has the multi-select
  // options ready without per-edit-open fetches.
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      CLIENT_OPTIONS.map(async (c) => {
        try {
          const portfolios = await fetchPortfolios(c.id);
          return [c.id, portfolios] as const;
        } catch {
          return [c.id, [] as PortfolioDef[]] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) {
        setPortfoliosByClient(Object.fromEntries(pairs));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const buildInitialFormState = (user: User | null): UserFormState => {
    if (!user) {
      return {
        email: '',
        password: '',
        display_name: '',
        role: 'dwa_standard',
        client_access: [],
        is_active: true,
        permissions: [],
      };
    }
    return {
      email: user.email,
      password: '',
      display_name: user.display_name || '',
      role: user.role,
      client_access: user.client_access ? [...user.client_access] : [],
      is_active: user.is_active,
      permissions: [...(user.permissions ?? [])],
    };
  };

  const handleSubmit = async (state: UserFormState) => {
    setSaving(true);
    setFormError(null);
    try {
      if (state.role === 'client' && state.client_access.length === 0) {
        throw new Error('Client-role users must have at least one client entry.');
      }
      if (creating) {
        if (!state.email || !state.password) {
          throw new Error('Email and initial password are required.');
        }
        if (state.password.length < 8) {
          throw new Error('Password must be at least 8 characters.');
        }
        const body: AdminCreateUserRequest = {
          email: state.email,
          password: state.password,
          display_name: state.display_name || undefined,
          role: state.role,
          client_access: clientAccessForSave(state.role, state.client_access),
        };
        if (state.role === 'dwa_admin' && state.permissions.length) body.permissions = state.permissions;
        await createUser(body);
      } else if (editing) {
        const body: AdminUpdateUserRequest = {
          display_name: state.display_name,
          is_active: state.is_active,
          role: state.role,
          client_access: clientAccessForSave(state.role, state.client_access),
        };
        // Send permissions only when they changed, so editing another field never
        // needs a super admin. Moving off dwa_admin drops them server-side.
        const before = [...(editing.permissions ?? [])].sort().join(',');
        const after = [...state.permissions].sort().join(',');
        if (state.role === 'dwa_admin' && before !== after) body.permissions = state.permissions;
        if (state.password) {
          if (state.password.length < 8) {
            throw new Error('New password must be at least 8 characters.');
          }
          body.password = state.password;
        }
        await updateUser(editing.id, body);
      }
      setEditing(null);
      setCreating(false);
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (user: User) => {
    if (user.id === currentUserId) return;
    if (!window.confirm(`Deactivate ${user.email}? They will no longer be able to log in.`)) return;
    try {
      await deactivateUser(user.id);
      await reload();
    } catch (e) {
      alert(`Failed to deactivate: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      // DWA admins first, then DWA standard, then clients. Within each, by email.
      const rank: Record<UserRole, number> = { dwa_admin: 0, dwa_standard: 1, client: 2 };
      const da = rank[a.role] ?? 99;
      const db = rank[b.role] ?? 99;
      if (da !== db) return da - db;
      return a.email.localeCompare(b.email);
    });
  }, [users]);

  // Sub-tab toggle button styling (shared between Users / PDF Recipients).
  const subTabButton = (active: boolean): React.CSSProperties => ({
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    padding: '0.4rem 0.95rem',
    borderRadius: 3,
    cursor: 'pointer',
    fontFamily: 'inherit',
    background: active ? '#1a202c' : 'transparent',
    color: active ? '#ffffff' : '#64748b',
    border: `1px solid ${active ? '#1a202c' : '#cbd5e1'}`,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Sub-tab strip — keeps Users and PDF Recipients nested in one tab. */}
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <button
          type="button"
          style={subTabButton(subTab === 'users')}
          onClick={() => setSubTab('users')}
        >
          Users
        </button>
        <button
          type="button"
          style={subTabButton(subTab === 'recipients')}
          onClick={() => setSubTab('recipients')}
        >
          PDF Recipients
        </button>
      </div>

      {subTab === 'recipients' && <EmailRecipientsPanel />}

      {subTab === 'users' && (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1a202c' }}>User Management</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label style={{ fontSize: '0.8rem', color: '#64748b', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              style={{ marginRight: '0.4rem' }}
            />
            Show inactive
          </label>
          <button
            type="button"
            style={button('primary')}
            onClick={() => { setCreating(true); setEditing(null); setFormError(null); }}
          >
            + New User
          </button>
        </div>
      </div>

      {creating && (
        <UserForm
          mode="create"
          initial={buildInitialFormState(null)}
          portfoliosByClient={portfoliosByClient}
          isSelf={false}
          canGrant={canGrant}
          saving={saving}
          error={formError}
          onCancel={() => { setCreating(false); setFormError(null); }}
          onSubmit={handleSubmit}
        />
      )}

      {editing && (
        <UserForm
          mode="edit"
          initial={buildInitialFormState(editing)}
          portfoliosByClient={portfoliosByClient}
          isSelf={editing.id === currentUserId}
          canGrant={canGrant}
          saving={saving}
          error={formError}
          onCancel={() => { setEditing(null); setFormError(null); }}
          onSubmit={handleSubmit}
        />
      )}

      <div style={card}>
        {loading && <p style={{ color: '#64748b' }}>Loading…</p>}
        {error && <p style={{ color: '#dc2626' }}>{error}</p>}
        {!loading && !error && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Display Name</th>
                <th style={th}>Role</th>
                <th style={th}>Access</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} style={{ background: u.is_active ? 'transparent' : '#fef2f2' }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.display_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={td}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em',
                      textTransform: 'uppercase', color: u.role === 'dwa_admin' ? '#A06820' : '#64748b',
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ ...td, fontSize: '0.78rem', color: '#4a5568' }}>
                    {summarizeAccess(u)}
                  </td>
                  <td style={td}>
                    {u.is_active
                      ? <span style={{ color: '#15803d', fontSize: '0.78rem' }}>active</span>
                      : <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>inactive</span>}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button
                        type="button"
                        style={button('secondary')}
                        onClick={() => { setEditing(u); setCreating(false); setFormError(null); }}
                      >
                        Edit
                      </button>
                      {u.is_active && u.id !== currentUserId && (
                        <button
                          type="button"
                          style={button('danger')}
                          onClick={() => handleDeactivate(u)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 && (
                <tr>
                  <td style={td} colSpan={6}>
                    <span style={{ color: '#94a3b8' }}>No users found.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default UsersTab;
